import { useState, useMemo } from "react";
import { useTabNavigate } from "@/hooks/useTabNavigate";
import { useVendors, getComplianceStatus } from "@/hooks/useVendors";
import { usePermissions } from "@/hooks/usePermissions";
import { usePagination } from "@/hooks/usePagination";
import { use1099Warnings } from "@/hooks/use1099Summary";
import { useVendorDuplicates } from "@/hooks/useVendorDuplicates";
import { PaginationControls } from "@/components/controller/PaginationControls";
import { NewVendorDialog } from "@/components/controller/NewVendorDialog";
import { VendorMergeDialog } from "@/components/controller/vendors/VendorMergeDialog";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { Search, Plus, Eye, AlertTriangle, FileText, FileSpreadsheet, Printer, GitMerge, X } from "lucide-react";
import { format } from "date-fns";
import { EntityContextMenu } from "@/components/shared/EntityContextMenu";
import { RequirePermission } from "@/components/RequirePermission";
import { PageActions } from "@/components/shared/PageActions";
import { useExportActions } from "@/lib/export";

const TYPE_LABELS: Record<string, string> = {
  subcontractor: "👷 Sub",
  material_supplier: "🧱 Supplier",
  equipment_rental: "🏗 Equipment",
  general: "📋 General",
};

const VendorsInner = () => {
  const [search, setSearch] = useState("");
  const pagination = usePagination();
  const { data: vendors, isLoading } = useVendors(pagination, search);
  const { data: w9Warnings = [] } = use1099Warnings();
  const { duplicateIds, allVendors } = useVendorDuplicates();
  const navigate = useTabNavigate();
  const { can } = usePermissions();
  const [complianceIssuesOnly, setComplianceIssuesOnly] = useState(false);
  const [is1099Only, setIs1099Only] = useState(false);
  const [duplicatesOnly, setDuplicatesOnly] = useState(false);
  const [newVendorOpen, setNewVendorOpen] = useState(false);
  const [mergeMode, setMergeMode] = useState(false);
  const [mergeSelected, setMergeSelected] = useState<Set<string>>(new Set());
  const [mergeDialogOpen, setMergeDialogOpen] = useState(false);
  const { doExport } = useExportActions({ entityType: "vendors" });

  const filtered = useMemo(() => {
    // When duplicates filter is on, use the full vendor list (not paginated subset)
    // because duplicates are detected across ALL vendors.
    let list = duplicatesOnly ? (allVendors || []) : (vendors || []);
    if (duplicatesOnly) {
      list = list.filter((v) => duplicateIds.has(v.id));
      const s = search.trim().toLowerCase();
      if (s) {
        list = list.filter((v) =>
          [v.vendor_name, v.email, v.phone, v.contact_name]
            .filter(Boolean)
            .some((f) => String(f).toLowerCase().includes(s))
        );
      }
    }
    if (complianceIssuesOnly) list = list.filter((v) => getComplianceStatus(v).level !== "green");
    if (is1099Only) list = list.filter((v) => v.is_1099_eligible);
    return list;
  }, [vendors, allVendors, complianceIssuesOnly, is1099Only, duplicatesOnly, duplicateIds, search]);

  const hasData = filtered.length > 0;
  const canMerge = can("manage_vendors");

  const toggleMergeSelect = (id: string) => {
    setMergeSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else if (next.size < 2) next.add(id);
      return next;
    });
  };

  const exitMergeMode = () => {
    setMergeMode(false);
    setMergeSelected(new Set());
  };

  // Resolve selected vendor pair from all vendors (so it works across pages/filters)
  const selectedPair = useMemo(() => {
    if (mergeSelected.size !== 2) return null;
    const ids = Array.from(mergeSelected);
    const all = [...(vendors || []), ...allVendors];
    const seen = new Map<string, any>();
    for (const v of all) if (!seen.has(v.id)) seen.set(v.id, v);
    const a = seen.get(ids[0]);
    const b = seen.get(ids[1]);
    if (!a || !b) return null;
    return { a, b };
  }, [mergeSelected, vendors, allVendors]);

  const pageActions = [
    {
      label: "New Vendor",
      icon: Plus,
      onClick: () => setNewVendorOpen(true),
      permission: can("manage_vendors"),
    },
    {
      label: mergeMode ? "Exit Merge Mode" : "Merge Vendors",
      icon: GitMerge,
      onClick: () => (mergeMode ? exitMergeMode() : setMergeMode(true)),
      permission: canMerge,
    },
    {
      label: "1099 Report",
      icon: FileText,
      onClick: () => navigate("/controller/vendors/1099-report"),
    },
    {
      label: "Export to Excel",
      icon: FileSpreadsheet,
      onClick: () => hasData && doExport("excel", filtered),
      permission: hasData,
      separator: true,
    },
    {
      label: "Export to CSV",
      icon: FileText,
      onClick: () => hasData && doExport("csv", filtered),
      permission: hasData,
    },
    {
      label: "Print",
      icon: Printer,
      onClick: () => hasData && doExport("print", filtered),
      permission: hasData,
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold tracking-tight">Vendors</h1>
        <PageActions actions={pageActions} />
      </div>

      {w9Warnings.length > 0 && (
        <div className="flex items-center gap-2 rounded-lg border border-destructive/30 bg-destructive/5 p-3">
          <AlertTriangle className="h-5 w-5 text-destructive shrink-0" />
          <p className="text-sm text-destructive font-medium">
            {w9Warnings.length} vendor{w9Warnings.length > 1 ? "s" : ""} over $600 YTD with no W-9 on file —{" "}
            <button className="underline" onClick={() => navigate("/controller/vendors/1099-report")}>
              View 1099 Report
            </button>
          </p>
        </div>
      )}

      {mergeMode && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-primary/30 bg-primary/5 p-3">
          <div className="flex items-center gap-2 text-sm">
            <GitMerge className="h-4 w-4 text-primary" />
            <span className="font-medium">Merge mode:</span>
            <span className="text-muted-foreground">
              Select 2 vendors to merge ({mergeSelected.size}/2 selected)
            </span>
          </div>
          <div className="flex gap-2">
            <Button
              size="sm"
              disabled={mergeSelected.size !== 2 || !selectedPair}
              onClick={() => setMergeDialogOpen(true)}
            >
              <GitMerge className="h-4 w-4 mr-1" /> Merge Selected
            </Button>
            <Button size="sm" variant="ghost" onClick={exitMergeMode}>
              <X className="h-4 w-4 mr-1" /> Cancel
            </Button>
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-4">
        <div className="relative max-w-sm flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search vendors…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <div className="flex items-center gap-2">
          <Switch id="compliance-filter" checked={complianceIssuesOnly} onCheckedChange={setComplianceIssuesOnly} />
          <Label htmlFor="compliance-filter" className="text-sm">Compliance issues only</Label>
        </div>
        <div className="flex items-center gap-2">
          <Switch id="1099-filter" checked={is1099Only} onCheckedChange={setIs1099Only} />
          <Label htmlFor="1099-filter" className="text-sm">1099 Required</Label>
        </div>
        {duplicateIds.size > 0 && (
          <div className="flex items-center gap-2">
            <Switch id="dup-filter" checked={duplicatesOnly} onCheckedChange={setDuplicatesOnly} />
            <Label htmlFor="dup-filter" className="text-sm">
              Duplicates only{" "}
              <Badge variant="secondary" className="ml-1 text-[10px]">{duplicateIds.size}</Badge>
            </Label>
          </div>
        )}
      </div>

      {isLoading ? (
        <p className="text-muted-foreground">Loading…</p>
      ) : !filtered?.length ? (
        <p className="text-muted-foreground">No vendors found.</p>
      ) : (
        <>
          {/* Mobile card view */}
          <div className="space-y-3 md:hidden">
            {filtered.map((v) => {
              const types = v.vendor_types || [v.vendor_type || "general"];
              const cs = (v as any).compliance_status || "pending";
              const csEmoji = cs === "approved" ? "🟢" : cs === "suspended" ? "⚫" : cs === "incomplete" ? "🔴" : "🟡";
              const isDup = duplicateIds.has(v.id);
              const isChecked = mergeSelected.has(v.id);
              return (
                <Card
                  key={v.id}
                  className={`cursor-pointer transition-colors ${
                    isChecked ? "border-primary bg-primary/5" : isDup ? "border-amber-300 bg-amber-50/40 dark:bg-amber-950/20 hover:bg-amber-100/40" : "hover:bg-muted/50"
                  }`}
                  onClick={() => mergeMode ? toggleMergeSelect(v.id) : navigate(`/controller/vendors/${v.id}`)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-2">
                      {mergeMode && (
                        <Checkbox
                          checked={isChecked}
                          onCheckedChange={() => toggleMergeSelect(v.id)}
                          onClick={(e) => e.stopPropagation()}
                          disabled={!isChecked && mergeSelected.size >= 2}
                          className="mt-1"
                        />
                      )}
                      <div className="min-w-0 flex-1">
                         <div className="flex items-center gap-2 mb-1">
                          <span>{csEmoji}</span>
                          <span className="font-mono text-xs text-muted-foreground">{(v as any).vendor_number ?? ""}</span>
                          <p className="font-medium truncate">{v.name}</p>
                          {isDup && <AlertTriangle className="h-3.5 w-3.5 text-amber-500 shrink-0" />}
                        </div>
                        <div className="flex gap-1 flex-wrap mb-1">
                          {types.map((t: string) => (
                            <Badge key={t} variant="outline" className="text-[10px]">{TYPE_LABELS[t] || t}</Badge>
                          ))}
                        </div>
                        <p className="text-sm text-muted-foreground">{v.contact_name || "No contact"}</p>
                      </div>
                      {!mergeMode && <Eye className="h-4 w-4 text-muted-foreground shrink-0 mt-1" />}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Desktop table */}
          <div className="hidden md:block">
            <Table>
              <TableHeader>
                <TableRow>
                  {mergeMode && <TableHead className="w-10"></TableHead>}
                  <TableHead className="w-8"></TableHead>
                  <TableHead>ID</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Payment Terms</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((v) => {
                  const compliance = getComplianceStatus(v);
                  const types = v.vendor_types || [v.vendor_type || "general"];
                  const cs = (v as any).compliance_status || "pending";
                  const csEmoji = cs === "approved" ? "🟢" : cs === "suspended" ? "⚫" : cs === "incomplete" ? "🔴" : "🟡";
                  const payTermsLabel = v.payment_terms === "custom" && (v as any).payment_terms_custom_days
                    ? `Net ${(v as any).payment_terms_custom_days}`
                    : (v.payment_terms || "net_30").replace("_", " ");
                  const isDup = duplicateIds.has(v.id);
                  const isChecked = mergeSelected.has(v.id);
                  return (
                    <EntityContextMenu key={v.id} entityType="vendors" entityId={v.id} data={v}>
                    <TableRow
                      className={`cursor-pointer transition-colors ${
                        isChecked ? "bg-primary/10" : isDup ? "bg-amber-50/50 dark:bg-amber-950/20 hover:bg-amber-100/50" : "hover:bg-muted/50"
                      }`}
                      onClick={() => mergeMode ? toggleMergeSelect(v.id) : navigate(`/controller/vendors/${v.id}`)}
                    >
                      {mergeMode && (
                        <TableCell className="pr-0" onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            checked={isChecked}
                            onCheckedChange={() => toggleMergeSelect(v.id)}
                            disabled={!isChecked && mergeSelected.size >= 2}
                          />
                        </TableCell>
                      )}
                      <TableCell>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="text-sm">{csEmoji}</span>
                          </TooltipTrigger>
                          <TooltipContent>
                            {cs === "approved"
                              ? "Compliance approved — all docs current"
                              : cs === "suspended"
                              ? "Vendor suspended"
                              : compliance.issues.length
                              ? compliance.issues.map((i) => i.label).join(", ")
                              : `Compliance: ${cs}`}
                          </TooltipContent>
                        </Tooltip>
                      </TableCell>
                      <TableCell className="font-mono text-sm text-muted-foreground">{(v as any).vendor_number ?? "—"}</TableCell>
                      <TableCell className="font-medium">
                        <span className="flex items-center gap-1.5">
                          {v.name}
                          {isDup && <AlertTriangle className="h-3.5 w-3.5 text-amber-500 shrink-0" />}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1 flex-wrap">
                          {types.map((t: string) => (
                            <Badge key={t} variant="outline" className="text-[10px]">{TYPE_LABELS[t] || t}</Badge>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">{v.contact_name || "—"}</TableCell>
                      <TableCell className="text-sm capitalize">{payTermsLabel}</TableCell>
                      <TableCell className="text-sm">{v.created_at ? format(new Date(v.created_at), "MMM d, yyyy") : "—"}</TableCell>
                      <TableCell>
                        <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); navigate(`/controller/vendors/${v.id}`); }}>
                          <Eye className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                    </EntityContextMenu>
                  );
                })}
              </TableBody>
            </Table>
          </div>
          <PaginationControls pagination={pagination} />
        </>
      )}

      <NewVendorDialog open={newVendorOpen} onOpenChange={setNewVendorOpen} />

      {selectedPair && (
        <VendorMergeDialog
          open={mergeDialogOpen}
          onOpenChange={(o) => {
            setMergeDialogOpen(o);
            if (!o) exitMergeMode();
          }}
          vendorA={selectedPair.a}
          vendorB={selectedPair.b}
        />
      )}
    </div>
  );
};

const Vendors = () => (
  <RequirePermission anyOf={["manage_vendors", "view_projects"]}>
    <VendorsInner />
  </RequirePermission>
);

export default Vendors;
